import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '/v1';

/**
 * AccountPage — Subscriber self-service account management.
 *
 * Sections:
 *   1. Profile        — company name, email, product type
 *   2. Current Plan   — plan name, status badge (Active / Cancelling / Canceled)
 *   3. Billing Cycle  — next billing date / trial end / access-until date
 *   4. Payment Method — card last 4 digits (from Stripe)
 *   5. Cancel / Undo Cancel subscription
 *
 * Cancel logic:
 *   - Trial users (status=trialing): access until trial_end (Day 45)
 *   - Paid subscribers (status=active): access until current_period_end (30 days)
 *   - Uses Stripe cancel_at_period_end=True — NEVER immediate cancellation
 *   - Undo Cancel available while status=cancelling (before period ends)
 */
const AccountPage = ({ session, onSessionUpdate }) => {
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [actionMessage, setActionMessage] = useState(null); // { type: 'success'|'error', text }
  const [licenseData, setLicenseData] = useState(null);

  useEffect(() => {
    fetchAccountData();
    fetchLicenseData();
  }, []); // eslint-disable-line

  const fetchLicenseData = async () => {
    try {
      const res = await fetch(`${API_BASE}/clients/${session.client_id}/license`);
      if (res.ok) {
        const data = await res.json();
        setLicenseData(data);
      }
    } catch { /* non-blocking */ }
  };

  const fetchAccountData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/subscribers/${session.client_id}/account`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setAccountData(data);
      } else {
        // Fallback: build from localStorage session
        setAccountData(buildFallbackData(session));
      }
    } catch {
      setAccountData(buildFallbackData(session));
    } finally {
      setLoading(false);
    }
  };

  const buildFallbackData = (s) => ({
    company_name: s.company_name || '—',
    email: s.email || '—',
    product_type: s.product_type || '—',
    product_name: s.product_name || '—',
    plan: s.plan || 'growth',
    subscription_status: s.payment_validated ? 'trialing' : 'pending',
    trial_end: s.trial_end || null,
    current_period_end: null,
    cancel_at_period_end: false,
    cancel_at: null,
    card_last4: null,
    card_brand: null,
    stripe_subscription_id: null,
    client_id: s.client_id,
  });

  // ── Cancel subscription ──────────────────────────────────────────────────
  const handleCancel = async () => {
    setCancelLoading(true);
    setActionMessage(null);
    try {
      const res = await fetch(`${API_BASE}/subscribers/${session.client_id}/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.status === 'ok') {
        setAccountData(prev => ({
          ...prev,
          subscription_status: 'cancelling',
          cancel_at_period_end: true,
          cancel_at: data.cancel_at,
          access_until: data.access_until,
        }));
        setActionMessage({
          type: 'success',
          text: `Subscription cancelled. You retain full access until ${formatDate(data.access_until)}.`,
        });
        if (onSessionUpdate) onSessionUpdate({ ...session, subscription_status: 'cancelling' });
      } else {
        setActionMessage({ type: 'error', text: data.message || 'Cancellation failed. Please try again.' });
      }
    } catch {
      setActionMessage({ type: 'error', text: 'Could not reach server. Please try again.' });
    } finally {
      setCancelLoading(false);
      setShowCancelConfirm(false);
    }
  };

  // ── Undo cancel (reactivate before period ends) ──────────────────────────
  const handleUndoCancel = async () => {
    setUndoLoading(true);
    setActionMessage(null);
    try {
      const res = await fetch(`${API_BASE}/subscribers/${session.client_id}/undo-cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.status === 'ok') {
        setAccountData(prev => ({
          ...prev,
          subscription_status: data.subscription_status || 'active',
          cancel_at_period_end: false,
          cancel_at: null,
        }));
        setActionMessage({ type: 'success', text: 'Cancellation reversed. Your subscription is active again.' });
        if (onSessionUpdate) onSessionUpdate({ ...session, subscription_status: 'active' });
      } else {
        setActionMessage({ type: 'error', text: data.message || 'Could not reverse cancellation.' });
      }
    } catch {
      setActionMessage({ type: 'error', text: 'Could not reach server. Please try again.' });
    } finally {
      setUndoLoading(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return iso; }
  };

  const getStatusBadge = (status, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd || status === 'cancelling') {
      return { label: '🟡 Cancelling', bg: '#FFF8E1', color: '#E65100', border: '#FFD54F' };
    }
    // Waiver-approved accounts show special badge
    if (accountData?.waiver_type === 'admin_alias_waiver') {
      return { label: '🟢 Waiver — 45-Day Trial', bg: '#E8F5E9', color: '#1B5E20', border: '#A5D6A7' };
    }
    switch (status) {
      case 'active':
        return { label: '🟢 Active', bg: '#E8F5E9', color: '#1B5E20', border: '#A5D6A7' };
      case 'trialing':
        return { label: '🟢 Trial Active', bg: '#E3F2FD', color: '#0D47A1', border: '#90CAF9' };
      case 'canceled':
      case 'cancelled':
        return { label: '🔴 Canceled', bg: '#FFEBEE', color: '#B71C1C', border: '#EF9A9A' };
      case 'pending':
        return { label: '⏳ Pending Payment', bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' };
      default:
        return { label: status, bg: '#F5F5F5', color: '#333', border: '#DDD' };
    }
  };

  const getAccessUntilDate = (data) => {
    if (data.cancel_at) return data.cancel_at;
    // Waiver accounts: calculate from trial_start + trial_days
    if (data.waiver_type === 'admin_alias_waiver' && data.trial_start) {
      const start = new Date(data.trial_start);
      const days = data.trial_days || 45;
      start.setDate(start.getDate() + days);
      return start.toISOString();
    }
    if (data.subscription_status === 'trialing' && data.trial_end) return data.trial_end;
    if (data.current_period_end) return data.current_period_end;
    return null;
  };

  const isCancellable = (status) =>
    ['active', 'trialing'].includes(status) && !accountData?.cancel_at_period_end;

  const isUndoable = (status, cancelAtPeriodEnd) =>
    cancelAtPeriodEnd || status === 'cancelling';

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
        Loading account details...
      </div>
    );
  }

  const d = accountData;
  const statusBadge = getStatusBadge(d.subscription_status, d.cancel_at_period_end);
  const accessUntil = getAccessUntilDate(d);

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '24px' }}>
      <h2 style={{ fontSize: '22px', color: '#1a1a2e', marginBottom: '4px' }}>👤 Account</h2>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '28px' }}>
        Manage your profile, subscription, and billing details.
      </p>

      {/* Action message banner */}
      {actionMessage && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px',
          background: actionMessage.type === 'success' ? '#E8F5E9' : '#FFEBEE',
          border: `1px solid ${actionMessage.type === 'success' ? '#A5D6A7' : '#EF9A9A'}`,
          color: actionMessage.type === 'success' ? '#1B5E20' : '#B71C1C',
        }}>
          {actionMessage.type === 'success' ? '✅' : '❌'} {actionMessage.text}
        </div>
      )}

      {/* ── Section 1: Profile ── */}
      <Section title="👤 Profile">
        <Row label="Company Name" value={d.company_name} />
        <Row label="Email" value={d.email} />
        <Row label="Product" value={d.product_name || d.product_type} />
        <Row label="Client ID" value={<code style={{ fontSize: '11px', background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>{d.client_id}</code>} />
      </Section>

      {/* ── Section 2: Current Plan ── */}
      <Section title="📋 Current Plan">
        <Row label="Plan" value={
          <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>
            {d.plan === 'growth' ? 'Growth — $49/month' : d.plan}
          </span>
        } />
        <Row label="Status" value={
          <span style={{
            display: 'inline-block', padding: '3px 12px', borderRadius: '12px', fontSize: '12px',
            fontWeight: 700, background: statusBadge.bg, color: statusBadge.color,
            border: `1px solid ${statusBadge.border}`,
          }}>
            {statusBadge.label}
          </span>
        } />
        {d.cancel_at_period_end && (
          <Row label="Cancels On" value={
            <span style={{ color: '#E65100', fontWeight: 600 }}>{formatDate(accessUntil)}</span>
          } />
        )}
      </Section>

      {/* ── Section 3: Billing Cycle ── */}
      <Section title="📅 Billing Cycle">
        {licenseData && licenseData.next_payment_date && (
          <Row label="Next Payment Due" value={
            <span style={{ fontWeight: 700, color: '#1565C0' }}>
              {licenseData.next_payment_date}
            </span>
          } />
        )}
        {licenseData && licenseData.daysLeft != null && licenseData.status === 'ACTIVE' && (
          <Row label="Days Remaining" value={
            <span style={{ fontWeight: 600, color: '#2E7D32' }}>
              {licenseData.daysLeft} days
            </span>
          } />
        )}
        {d.subscription_status === 'trialing' && (
          <Row label="Trial Period" value={
            <span>Days 1–45 (free) → <strong>$49/month from Day 46</strong></span>
          } />
        )}
        {d.trial_end && (
          <Row label="Trial Ends" value={formatDate(d.trial_end)} />
        )}
        {d.current_period_end && d.subscription_status !== 'trialing' && (
          <Row label="Next Billing Date" value={formatDate(d.current_period_end)} />
        )}
        {accessUntil && (
          <Row label="Access Until" value={
            <span style={{ fontWeight: 600, color: d.cancel_at_period_end ? '#E65100' : '#1B5E20' }}>
              {formatDate(accessUntil)}
            </span>
          } />
        )}
        {!d.cancel_at_period_end && d.subscription_status === 'active' && (
          <Row label="Billing Cycle" value="Monthly (30 days)" />
        )}
      </Section>

      {/* ── Section 4: Payment Method ── */}
      <Section title="💳 Payment Method">
        {d.card_last4 ? (
          <>
            <Row label="Card" value={
              <span>
                {d.card_brand ? <strong style={{ textTransform: 'capitalize' }}>{d.card_brand}</strong> : 'Card'} ending in <strong>••••{d.card_last4}</strong>
              </span>
            } />
          </>
        ) : (
          <Row label="Card" value={
            <span style={{ color: '#999', fontSize: '12px' }}>
              {d.subscription_status === 'pending'
                ? '⚠️ No payment method on file — complete payment validation to activate trial'
                : 'Card on file (details managed via Stripe)'}
            </span>
          } />
        )}
      </Section>

      {/* ── Section 5: Cancel / Undo ── */}
      <Section title="⚙️ Subscription Actions">
        {/* Status-specific messaging */}
        {d.subscription_status === 'trialing' && !d.cancel_at_period_end && (
          <div style={{ padding: '10px 14px', background: '#E3F2FD', borderRadius: '8px', fontSize: '12px', color: '#0D47A1', marginBottom: '16px' }}>
            📋 You are in your <strong>45-day free trial</strong>. If you cancel now, you retain full access until <strong>{formatDate(d.trial_end)}</strong>. You will not be charged.
          </div>
        )}
        {d.subscription_status === 'active' && !d.cancel_at_period_end && (
          <div style={{ padding: '10px 14px', background: '#F3E5F5', borderRadius: '8px', fontSize: '12px', color: '#4A148C', marginBottom: '16px' }}>
            📋 If you cancel, your subscription remains active until <strong>{formatDate(d.current_period_end)}</strong> (end of current 30-day billing cycle). No further charges.
          </div>
        )}
        {d.cancel_at_period_end && (
          <div style={{ padding: '10px 14px', background: '#FFF8E1', borderRadius: '8px', fontSize: '12px', color: '#E65100', marginBottom: '16px', border: '1px solid #FFD54F' }}>
            🟡 <strong>Cancellation scheduled.</strong> Your subscription will end on <strong>{formatDate(accessUntil)}</strong>. You have full access until then. Changed your mind?
          </div>
        )}
        {(d.subscription_status === 'canceled' || d.subscription_status === 'cancelled') && (
          <div style={{ padding: '10px 14px', background: '#FFEBEE', borderRadius: '8px', fontSize: '12px', color: '#B71C1C', marginBottom: '16px' }}>
            🔴 <strong>Subscription ended.</strong> Your access has been revoked. Contact support to reactivate.
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {/* Cancel button */}
          {isCancellable(d.subscription_status) && !showCancelConfirm && (
            <button
              onClick={() => setShowCancelConfirm(true)}
              style={{
                padding: '10px 20px', background: '#fff', color: '#C62828',
                border: '2px solid #EF9A9A', borderRadius: '8px', fontSize: '13px',
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              🚫 Cancel Subscription
            </button>
          )}

          {/* Undo Cancel button */}
          {isUndoable(d.subscription_status, d.cancel_at_period_end) && (
            <button
              onClick={handleUndoCancel}
              disabled={undoLoading}
              style={{
                padding: '10px 20px', background: '#E8F5E9', color: '#1B5E20',
                border: '2px solid #A5D6A7', borderRadius: '8px', fontSize: '13px',
                fontWeight: 600, cursor: undoLoading ? 'wait' : 'pointer',
                opacity: undoLoading ? 0.7 : 1,
              }}
            >
              {undoLoading ? '⏳ Reversing...' : '↩️ Undo Cancellation'}
            </button>
          )}
        </div>

        {/* Confirmation dialog */}
        {showCancelConfirm && (
          <div style={{
            marginTop: '16px', padding: '20px', background: '#FFEBEE',
            border: '2px solid #EF9A9A', borderRadius: '10px',
          }}>
            <div style={{ fontWeight: 700, color: '#B71C1C', marginBottom: '8px', fontSize: '14px' }}>
              ⚠️ Confirm Cancellation
            </div>
            <div style={{ fontSize: '13px', color: '#333', marginBottom: '16px', lineHeight: 1.6 }}>
              Your subscription will remain active until:
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>
                  {d.subscription_status === 'trialing'
                    ? <>End of trial — <strong>{formatDate(d.trial_end)}</strong> (Day 45)</>
                    : <>End of current billing cycle — <strong>{formatDate(d.current_period_end)}</strong> (30 days)</>
                  }
                </li>
              </ul>
              You will <strong>not be charged again</strong>. Are you sure?
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCancel}
                disabled={cancelLoading}
                style={{
                  padding: '9px 20px', background: '#C62828', color: '#fff',
                  border: 'none', borderRadius: '7px', fontSize: '13px',
                  fontWeight: 700, cursor: cancelLoading ? 'wait' : 'pointer',
                  opacity: cancelLoading ? 0.7 : 1,
                }}
              >
                {cancelLoading ? '⏳ Cancelling...' : 'Yes, Cancel'}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                style={{
                  padding: '9px 20px', background: '#fff', color: '#333',
                  border: '1px solid #ddd', borderRadius: '7px', fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                No, Keep Subscription
              </button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div style={{
    background: '#fff', border: '1px solid #e8e8e8', borderRadius: '12px',
    marginBottom: '20px', overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }}>
    <div style={{
      padding: '12px 20px', background: '#f8f9fa', borderBottom: '1px solid #e8e8e8',
      fontSize: '13px', fontWeight: 700, color: '#1a1a2e',
    }}>
      {title}
    </div>
    <div style={{ padding: '4px 0' }}>
      {children}
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 20px', borderBottom: '1px solid #f5f5f5', fontSize: '13px',
  }}>
    <span style={{ color: '#666', minWidth: '160px' }}>{label}</span>
    <span style={{ color: '#1a1a2e', textAlign: 'right' }}>{value}</span>
  </div>
);

export default AccountPage;
